import * as React from 'react';

import gql from 'graphql-tag';
import type { LocationState } from 'history';
import _ from 'lodash';
import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import { useLocation } from 'js/lib/useRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import withNotFound from 'bundles/common/components/withNotFound';
import { itemsPerPage } from 'bundles/directory-graphql/common/constants';
import LinksContainer from 'bundles/directory-graphql/components/LinksContainer';
import Metatags from 'bundles/directory-graphql/components/MetatagsWithPagination';
import NotFound from 'bundles/directory-graphql/components/NotFound';
import type {
  DirectoryPageSpecializationsQuery,
  DirectoryPageSpecializationsQueryVariables,
  DirectoryPageSpecializationsQuery_XdpV1Resource_getAll_elements_xdpMetadata_XdpV1_sdpMetadataMember_sdpMetadata as DirectoryS12n,
  DirectoryPageSpecializationsQuery_XdpV1Resource_getAll_elements_xdpMetadata_XdpV1_sdpMetadataMember as XdpV1S12n,
} from 'bundles/directory-graphql/components/__generated__/DirectoryPageSpecializationsQuery';
import usePaginationData from 'bundles/directory-graphql/hooks/usePaginationData';
import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import { s12nRoot } from 'bundles/s12n-common/service/constants';

import _t from 'i18n!nls/directory-graphql';

type PropsFromRouter = {
  page: string;
};

type PropsFromGraphql = {
  s12ns: Array<DirectoryS12n | null> | undefined;
  total: number;
};

type Props = PropsFromRouter & PropsFromGraphql;

type LocationWithHost = LocationState & {
  hostname: string;
};

export const Specializations = ({ page, total, s12ns }: Props) => {
  const location = useLocation();
  const hostname = (location as LocationWithHost).hostname;
  const { currentPage, pageCount } = usePaginationData(total, itemsPerPage);

  const additionalSettings = {
    title: _t('Specializations Directory'),
    description: _t(
      'The Specializations directory includes links to all of our #{total} Specialization description pages.',
      { total }
    ),
    canonicalLinkHrefOverride: `https://${hostname}/directory/specializations${
      currentPage === 1 ? '' : '?page=' + currentPage
    }`,
    disableCrawlerIndexing: true,
    useFollowForDisablingIndexing: true,
  };
  const actualS12ns = _.filter(s12ns, (s12n): s12n is DirectoryS12n & { slug: string } => !!s12n && !!s12n.slug);

  const titledLinks = _.sortBy(
    _.map(actualS12ns, ({ name, partners, slug, launchedAt }) => {
      const partnerNames = partners && partners.map(({ name: partnerName }) => partnerName).join(', ');
      const s12nLink = `${s12nRoot}/${slug}`;
      const displayName = launchedAt === null || !name ? 'Unlaunched Test S12N' : name.trim();
      return {
        title: `${displayName.trim()} (${partnerNames || 'Coursera'})`,
        link: s12nLink,
      };
    }),
    'title'
  );

  return (
    <div>
      <LinksContainer titledLinks={titledLinks} />
      <PaginationControls currentPage={currentPage} largeStyle pageCount={pageCount} maxPages={4} renderLinks={true} />
      <Metatags total={total} additionalSettings={additionalSettings} />
    </div>
  );
};

export default compose<Props, {}>(
  connectToRouter((router) => ({ page: router.location.query.page })),
  waitForGraphQL<
    PropsFromRouter,
    DirectoryPageSpecializationsQuery,
    DirectoryPageSpecializationsQueryVariables,
    PropsFromGraphql
  >(
    gql`
      query DirectoryPageSpecializationsQuery($start: String!, $limit: Int!) {
        XdpV1Resource {
          getAll(productType: "SPECIALIZATION", start: $start, limit: $limit) {
            elements {
              id
              xdpMetadata {
                ... on XdpV1_sdpMetadataMember {
                  sdpMetadata {
                    id
                    slug
                    name
                    launchedAt
                    partners {
                      id
                      name
                    }
                  }
                }
              }
            }
            paging {
              total
              next
            }
          }
        }
      }
    `,
    {
      props: ({ data }) => {
        const res = data?.XdpV1Resource?.getAll;
        const elements = res?.elements;
        const total = res?.paging?.total || 0;

        const s12ns = elements?.map((element) => (element?.xdpMetadata as XdpV1S12n)?.sdpMetadata);
        return {
          s12ns,
          total,
        };
      },
      options({ page = '1' }) {
        const pageInt = parseInt(page, 10);
        let pageToDisplay = pageInt;
        if (_.isNaN(pageToDisplay) || pageToDisplay < 1) {
          pageToDisplay = 1;
        }

        const start = (pageToDisplay - 1) * itemsPerPage;
        return {
          variables: {
            start: start.toString(),
            limit: itemsPerPage,
          },
        };
      },
    }
  ),
  withNotFound({
    test: ({ page = '1', total }) => {
      const pageInt = parseInt(page, 10);
      return _.isNaN(pageInt) || pageInt < 1 || pageInt > Math.ceil(total / itemsPerPage);
    },
    errorComponent: NotFound,
  })
)(Specializations);
